import { Alert, Button, Card, Checkbox, FileInput, Grid, Group, Select, Stack, Text, Textarea, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconSend } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { http } from "../../api/http";
import { useUser } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";

/** Eksterni korisnik: zahtjev za pristup podacima SVOG gazdinstva (odobrava Agencija ili opštinski referent). */
export default function AccessPage() {
  const { t } = useT();
  const user = useUser();
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ["my-access"], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/access-requests/mine") });
  const [bpg, setBpg] = useState("");
  const [holder, setHolder] = useState(user.name ?? "");
  const [relation, setRelation] = useState<string | null>("nosilac");
  const [msg, setMsg] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const validBpg = /^[0-9]{6,15}$/.test(bpg);

  async function send() {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("bpg", bpg);
      fd.append("holder_name", holder);
      fd.append("relation", relation ?? "nosilac");
      fd.append("consent", String(consent));
      if (msg) fd.append("message", msg);
      if (file) fd.append("file", file);
      const r = await http.form<{ poruka: string }>("/api/v1/access-requests", fd);
      notifications.show({ color: "green", message: t(r.poruka) });
      setBpg(""); setMsg(""); setFile(null); setConsent(false);
      qc.invalidateQueries({ queryKey: ["my-access"] });
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setBusy(false); }
  }

  return (
    <Stack>
      <PageHeader title="Pristup podacima mog gazdinstva" description="Kao nosilac ili ovlašćeno lice gazdinstva možete vidjeti svoje voćnjake, poljoprivrednu knjigu, fenologiju, fotografije i metriku stabala. Pristup se odobrava nakon provjere u Registru poljoprivrednih gazdinstava." />
      {user.bpg && user.bpg.length > 0 && (
        <Alert color="green" title={t("Pristup je odobren")}>{t("Gazdinstva")}: {user.bpg.join(", ")}. {t("Podaci su dostupni u meniju (Voćnjaci i stabla, Poljoprivredna knjiga).")}</Alert>
      )}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder>
            <Title order={5} mb="sm">{t("Novi zahtjev")}</Title>
            <Stack gap="sm">
              <TextInput label="BPG" description={t("Broj poljoprivrednog gazdinstva iz rješenja o upisu u RPG")} value={bpg}
                onChange={(e) => setBpg(e.currentTarget.value.replace(/\D/g, ""))} error={bpg && !validBpg ? t("6–15 cifara") : undefined} required />
              <TextInput label={t("Nosilac gazdinstva (ime i prezime)")} value={holder} onChange={(e) => setHolder(e.currentTarget.value)} required />
              <Select label={t("Moj odnos prema gazdinstvu")} value={relation} onChange={setRelation} data={[
                { value: "nosilac", label: t("Nosilac") }, { value: "clan", label: t("Član gazdinstva") }, { value: "ovlasceno_lice", label: t("Ovlašćeno lice") }]} />
              <FileInput label={t("Dokaz (preporučeno)")} description={t("Rješenje o upisu u RPG ili ovlašćenje; PDF/JPG do 20 MB")} value={file}
                onChange={setFile} clearable accept=".pdf,.jpg,.jpeg,.png" />
              <Textarea label={t("Napomena")} value={msg} onChange={(e) => setMsg(e.currentTarget.value)} autosize minRows={2} />
              <Alert variant="light" icon={<IconInfoCircle />} color="blue">
                <Text size="xs">{t("Obavještenje o obradi ličnih podataka: Agencija za agrarna plaćanja obrađuje vaše ime, e-poštu, BPG i priloženi dokaz isključivo radi provjere prava pristupa podacima gazdinstva (ZZLP BiH 12/25). Podaci se ne ustupaju trećim licima; čuvaju se 5 godina od odluke. Možete zatražiti uvid, ispravku ili brisanje kroz „Moji podaci (ZZLP)“. JMBG se ne prikuplja.")}</Text>
              </Alert>
              <Checkbox checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)}
                label={t("Upoznat/a sam sa obavještenjem o obradi ličnih podataka i potvrđujem tačnost navedenih podataka.")} />
              <Group justify="flex-end">
                <Button leftSection={<IconSend size={16} />} disabled={!validBpg || !holder || !consent} loading={busy} onClick={send}>{t("Pošalji zahtjev")}</Button>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder h="100%">
            <Title order={5} mb="sm">{t("Moji zahtjevi")}</Title>
            <DataTable rows={mine.data ?? []} search={false} columns={[
              { key: "created_at", label: "Podnesen", render: (r) => fmtDateTime(r.created_at) }, { key: "bpg", label: "BPG" },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
              { key: "decision_note", label: "Obrazloženje" }, { key: "decided_at", label: "Odlučeno", render: (r) => fmtDateTime(r.decided_at) },
            ]} />
            <Text size="xs" c="dimmed" mt="sm">{t("Nakon odobrenja odjavite se i ponovo prijavite da bi se pristup primijenio.")}</Text>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
